import React, { useEffect, useState } from 'react';
import { Card, Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';

import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import FormInput from '../../../components/form/FormInput';

import {
  apiKeysListGet,
  apiKeyAdd,
  apiKeyRevoke,
  apiKeyEnable,
  apiKeyDelete
} from '../../../store/slices/tenantSlices/settingSlice';

// CRM / CTI integration keys. A key lets a third-party CRM drive an agent's existing
// desktop session (manual call, mute, hold, hangup) over the API - it is NOT a login and
// does not affect the single-login rule.
const ApiKeys = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showGen, setShowGen] = useState(false);
  const [label, setLabel] = useState('');
  const [newKey, setNewKey] = useState(null); // full key shown once after creation

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await dispatch(apiKeysListGet()).unwrap();
      setRows(res || []);
    } catch (e) {
      console.log('api-keys load error', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleGenerate = async () => {
    try {
      const res = await dispatch(apiKeyAdd({ label })).unwrap();
      // show both full values once: api_key (control) + ws_token (socket)
      setNewKey(res ? { api_key: res.api_key, ws_token: res.ws_token } : null);
      setLabel('');
      await load();
    } catch (e) {
      toast.error('Failed to generate key');
    }
  };

  const handleRevoke = async (id) => {
    try {
      await dispatch(apiKeyRevoke(id)).unwrap();
      toast.success('Key revoked');
      await load();
    } catch (e) {
      toast.error('Failed to revoke');
    }
  };

  const handleEnable = async (id) => {
    try {
      await dispatch(apiKeyEnable(id)).unwrap();
      toast.success('Key enabled');
      await load();
    } catch (e) {
      toast.error('Failed to enable');
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(apiKeyDelete(id)).unwrap();
      toast.success('Key deleted');
      await load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };

  const closeGen = () => {
    setShowGen(false);
    setLabel('');
    setNewKey(null);
  };

  return (
    <div>
      <AdminHeader title="CRM API Keys" isAdd={false} />

      <Card className="p-0 m-0 mb-2">
        <div className="p-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
            <div className="fw-bold">CRM / CTI Integration Keys</div>
            <Button color="primary" size="sm" onClick={() => setShowGen(true)}>
              <Icon.Plus size={14} className="me-1" /> Generate Key
            </Button>
          </div>
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
            A key lets a third-party CRM control an agent&apos;s live desktop session via the API
            (manual call, mute, hold, hangup). Send it as the <code>X-Api-Key</code> header plus the
            Callex <code>agent_id</code> (the Users-page ID) in the body. It is <span className="fw-bold">
            not a login</span> and does not affect single-login. The agent must be logged into the
            desktop app for control actions to take effect.
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 340px)' }}>
          <CallexLoader />
        </Card>
      ) : (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 340px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>Label</th>
                  <th style={{ width: '28%' }}>Key</th>
                  <th style={{ width: '12%' }}>Status</th>
                  <th style={{ width: '18%' }}>Created</th>
                  <th style={{ width: '14%' }} className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      No keys yet
                    </td>
                  </tr>
                ) : (
                  rows.map((k) => (
                    <tr key={k.id}>
                      <td>{k.label}</td>
                      <td>
                        <div>
                          <code>{k.key_masked}</code>{' '}
                          <span className="text-muted" style={{ fontSize: '0.72rem' }}>api</span>
                        </div>
                        {k.ws_token_masked && (
                          <div>
                            <code>{k.ws_token_masked}</code>{' '}
                            <span className="text-muted" style={{ fontSize: '0.72rem' }}>socket</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            background: k.status ? '#e6f4ea' : '#fdeeea',
                            color: k.status ? '#0d7a3e' : '#c2410c',
                            borderRadius: 12,
                            padding: '2px 10px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                          {k.status ? 'Active' : 'Revoked'}
                        </span>
                      </td>
                      <td>{k.created_at ? new Date(k.created_at).toLocaleString() : '-'}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-3">
                          {k.status ? (
                            <Icon.Slash
                              size={16}
                              className="cursor-pointer text-warning"
                              title="Revoke"
                              onClick={() => handleRevoke(k.id)}
                            />
                          ) : (
                            <Icon.Check
                              size={16}
                              className="cursor-pointer text-success"
                              title="Enable"
                              onClick={() => handleEnable(k.id)}
                            />
                          )}
                          <Icon.Trash
                            size={16}
                            className="cursor-pointer text-danger"
                            title="Delete"
                            onClick={() => handleDelete(k.id)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Generate key modal */}
      <Modal isOpen={showGen} toggle={closeGen} centered>
        <ModalHeader toggle={closeGen}>Generate CRM API Key</ModalHeader>
        <ModalBody>
          {!newKey ? (
            <>
              <label className="form-label">Label (what it&apos;s for)</label>
              <FormInput
                type="text"
                value={label}
                placeholder="e.g. XYZ CRM integration"
                onChange={(e) => setLabel(e.target.value)}
              />
            </>
          ) : (
            <>
              <div className="fw-bold mb-2 text-success">Key created — copy both now</div>

              <div className="fw-bold" style={{ fontSize: '0.8rem' }}>
                API key <span className="text-muted">(control: dial / mute / hold / hangup — backend only, keep secret)</span>
              </div>
              <div
                className="d-flex align-items-center justify-content-between p-2 mb-2"
                style={{ background: '#f1f3f5', borderRadius: 6, wordBreak: 'break-all' }}>
                <code>{newKey.api_key}</code>
                <Icon.Copy
                  size={18}
                  className="cursor-pointer ms-2 text-primary"
                  title="Copy API key"
                  onClick={() => copy(newKey.api_key)}
                />
              </div>

              <div className="fw-bold" style={{ fontSize: '0.8rem' }}>
                Socket token <span className="text-muted">(read-only call events — safe for CRM browser clients)</span>
              </div>
              <div
                className="d-flex align-items-center justify-content-between p-2 mb-2"
                style={{ background: '#f1f3f5', borderRadius: 6, wordBreak: 'break-all' }}>
                <code>{newKey.ws_token}</code>
                <Icon.Copy
                  size={18}
                  className="cursor-pointer ms-2 text-primary"
                  title="Copy socket token"
                  onClick={() => copy(newKey.ws_token)}
                />
              </div>

              <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                Store these now — for security they will <span className="fw-bold">not be shown again</span>.
                Afterwards only the last few characters are visible.
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {!newKey ? (
            <>
              <Button color="secondary" onClick={closeGen}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleGenerate}>
                Generate
              </Button>
            </>
          ) : (
            <Button color="primary" onClick={closeGen}>
              Done
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ApiKeys;
